/**
 * Organisation access — what the SPA needs to recognise the BFF's access gate (#1809).
 *
 * The gate refuses every API call made on behalf of a suspended, closed or archived organisation,
 * with a 403 whose body carries this code. The SPA keys on the code, never on the message text,
 * to show "contact Site Compass" instead of a generic Access Denied.
 *
 * ⚠️ Duplicated, deliberately, from `ORGANISATION_UNAVAILABLE_CODE` in
 * `src/server/middleware/organisation-access.middleware.ts`: the SPA never imports from
 * `src/server`, which would pull server dependencies into the browser bundle. A contract test in
 * `tests/unit/stores/auth.store.test.ts` asserts the two are equal.
 *
 * @module utils/organisation-access
 */

export const ORGANISATION_UNAVAILABLE_CODE = 'ORGANISATION_UNAVAILABLE'

/** The gate's code for a member who holds no usable seat (#1848). Duplicated from the server like the one above. */
export const SEAT_REQUIRED_CODE = 'SEAT_REQUIRED'

/**
 * Which refusal, if any, did the access gate send? Keyed on the code, never the message, and the
 * body is read ONCE: a real `Response` body cannot be read twice, so callers that need both
 * answers ask here instead of running two predicates over one response.
 */
export async function gateRefusalCode(
  response: Response
): Promise<typeof ORGANISATION_UNAVAILABLE_CODE | typeof SEAT_REQUIRED_CODE | null> {
  if (response.status !== 403) return null
  try {
    const body: unknown = await response.json()
    const code =
      typeof body === 'object' && body !== null ? (body as { code?: unknown }).code : null
    return code === ORGANISATION_UNAVAILABLE_CODE || code === SEAT_REQUIRED_CODE ? code : null
  } catch {
    // Not JSON — so not the gate's denial, which always is.
    return null
  }
}

/** Did this response come from the access gate refusing a blocked organisation? */
export async function isOrganisationUnavailable(response: Response): Promise<boolean> {
  return (await gateRefusalCode(response)) === ORGANISATION_UNAVAILABLE_CODE
}

/** Did this response come from the access gate refusing a member who holds no usable seat? */
export async function isSeatRequired(response: Response): Promise<boolean> {
  return (await gateRefusalCode(response)) === SEAT_REQUIRED_CODE
}
